// Sistema de cache inteligente para datos geográficos
class GeoCache {
  constructor() {
    this.prefix = 'geoCache_';
    this.defaultTTL = 24 * 60 * 60 * 1000; // 24 horas (más persistente)
    this.maxCacheSize = 10 * 1024 * 1024; // 10MB máximo (aumentado)
    this.compressionThreshold = 256; // Comprimir datos > 256B (más agresivo)
    
    // Cache en memoria para acceso más rápido
    this.memoryCache = new Map();
    this.memoryCacheSize = 5 * 1024 * 1024; // 5MB para cache en memoria
    
    // Limpiar cache corrupto al inicializar
    this.initializeCache();
  }

  // Inicializar y limpiar cache corrupto
  initializeCache() {
        this.cleanupCorruptedCache();
      }

  // Generar clave única para bounds + zoom + filtros
  generateKey(type, bounds, zoom, filters = {}) {
    const boundsStr = bounds ? `${bounds.north}_${bounds.south}_${bounds.east}_${bounds.west}` : 'all';
    const filtersStr = Object.entries(filters)
      .filter(([key, value]) => value && value !== '')
      .map(([key, value]) => `${key}:${value}`)
      .sort()
      .join('|');
    
    return `${this.prefix}${type}_z${zoom}_${boundsStr}_${filtersStr}`;
  }

  // Comprimir datos JSON grandes
  compress(data) {
    const jsonStr = JSON.stringify(data);
    if (jsonStr.length < this.compressionThreshold) {
      return { data: jsonStr, compressed: false };
    }
    
    // Compresión más agresiva para reducir tamaño
    const compressed = jsonStr
      .replace(/,{"type":"Feature"/g, ',{T:F')
      .replace(/"type":"Feature"/g, 'T:F')
      .replace(/"geometry"/g, 'G')
      .replace(/"properties"/g, 'P')
      .replace(/"coordinates"/g, 'C')
      .replace(/"zipcode"/g, 'Z')
      .replace(/"total_population"/g, 'TP')
      .replace(/"hispanic_total"/g, 'HT')
      .replace(/"mexicano"/g, 'M')
      .replace(/"puerto_rico"/g, 'PR')
      .replace(/"cubano"/g, 'CU')
      .replace(/"colombia"/g, 'CO')
      .replace(/"dom_republic"/g, 'DR')
      .replace(/"guatemala"/g, 'GT')
      .replace(/"el_salvador"/g, 'ES')
      .replace(/"honduras"/g, 'HN')
      .replace(/"ecuador"/g, 'EC')
      .replace(/"peru"/g, 'PE')
      .replace(/"venezuela"/g, 'VE')
      .replace(/"espana"/g, 'SP')
      .replace(/"dma_code"/g, 'DC')
      .replace(/"estado"/g, 'E')
      .replace(/"region"/g, 'R');
    
    return { data: compressed, compressed: true };
  }

  // Descomprimir datos
  decompress(cachedData) {
    if (!cachedData.compressed) {
      return JSON.parse(cachedData.data);
    }
    
    let decompressed = cachedData.data;
    
    // Primero reemplazar los patrones más específicos
    decompressed = decompressed
      .replace(/,{T:F/g, ',{"type":"Feature"')
      .replace(/T:F/g, '"type":"Feature"')
      .replace(/TP/g, '"total_population"')
      .replace(/HT/g, '"hispanic_total"')
      .replace(/PR/g, '"puerto_rico"')
      .replace(/CU/g, '"cubano"')
      .replace(/CO/g, '"colombia"')
      .replace(/DR/g, '"dom_republic"')
      .replace(/GT/g, '"guatemala"')
      .replace(/ES/g, '"el_salvador"')
      .replace(/HN/g, '"honduras"')
      .replace(/EC/g, '"ecuador"')
      .replace(/PE/g, '"peru"')
      .replace(/VE/g, '"venezuela"')
      .replace(/SP/g, '"espana"')
      .replace(/DC/g, '"dma_code"');
    
    // Luego reemplazar los patrones más simples
    decompressed = decompressed
      .replace(/G/g, '"geometry"')
      .replace(/P/g, '"properties"')
      .replace(/C/g, '"coordinates"')
      .replace(/Z/g, '"zipcode"')
      .replace(/M/g, '"mexicano"')
      .replace(/E/g, '"estado"')
      .replace(/R/g, '"region"');
    
    try {
      return JSON.parse(decompressed);
    } catch (error) {
            throw error;
    }
  }

  // Guardar en cache
  set(key, data, ttl = this.defaultTTL) {
    try {
      const dataSize = JSON.stringify(data).length;
      const MAX_LOCALSTORAGE_SIZE = 1024 * 1024; // 1MB límite para localStorage
      
      // Guardar en memoria para acceso más rápido (siempre)
      this.memoryCache.set(key, {
        data: data, // Datos sin comprimir en memoria
        timestamp: Date.now(),
        ttl: ttl,
        size: dataSize
      });
      
      // Solo guardar en localStorage si los datos no son demasiado grandes
      if (dataSize < MAX_LOCALSTORAGE_SIZE) {
        const cacheEntry = {
          data: this.compress(data),
          timestamp: Date.now(),
          ttl: ttl,
          size: dataSize
        };

        try {
          localStorage.setItem(key, JSON.stringify(cacheEntry));
          this.cleanupIfNeeded();
        } catch (localStorageError) {
          // Si falla localStorage, solo mantener en memoria
        }
      } else {
        // Datos demasiado grandes, solo memoria
      }
      
      this.cleanupMemoryCache();
      return true;
    } catch (error) {
            this.cleanup(); // Limpiar cache si está lleno
      return false;
    }
  }

  // Obtener del cache
  get(key) {
    try {
      // Primero intentar desde memoria (más rápido)
      const memoryEntry = this.memoryCache.get(key);
      if (memoryEntry) {
        // Verificar si expiró
        if (Date.now() - memoryEntry.timestamp > memoryEntry.ttl) {
          this.memoryCache.delete(key);
          localStorage.removeItem(key);
          return null;
        }
        
        return memoryEntry.data;
      }

      // Si no está en memoria, intentar desde localStorage
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheEntry = JSON.parse(cached);
      
      // Verificar si expiró
      if (Date.now() - cacheEntry.timestamp > cacheEntry.ttl) {
        localStorage.removeItem(key);
        return null;
      }

      const data = this.decompress(cacheEntry.data);
      
      // Restaurar en memoria para futuros accesos
      this.memoryCache.set(key, {
        data: data,
        timestamp: cacheEntry.timestamp,
        ttl: cacheEntry.ttl,
        size: cacheEntry.size
      });
      
      return data;
    } catch (error) {
            // Limpiar entrada corrupta
      localStorage.removeItem(key);
      this.memoryCache.delete(key);
      // Limpiar cache corrupto
      this.cleanupCorruptedCache();
      return null;
    }
  }

  // Limpiar cache corrupto
  cleanupCorruptedCache() {
        let cleanedCount = 0;
    
    for (let key in localStorage) {
      if (key.startsWith(this.prefix)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          // Verificar que la estructura sea correcta
          if (!cached || typeof cached !== 'object' || !cached.data) {
            throw new Error('Estructura de cache inválida');
          }
          
          // Verificar que los datos comprimidos se puedan descomprimir
          if (cached.data && cached.data.compressed) {
            try {
              this.decompress(cached.data);
            } catch (decompressError) {
              throw new Error('Datos comprimidos corruptos');
            }
          }
        } catch (error) {
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
    }
  }

  // Limpiar cache si está lleno
  cleanupIfNeeded() {
    const cacheSize = this.getCacheSize();
    if (cacheSize > this.maxCacheSize) {
            this.cleanup();
    }
  }

  // Limpiar cache en memoria
  cleanupMemoryCache() {
    const now = Date.now();
    let totalSize = 0;
    const memoryEntries = [];

    // Calcular tamaño total y recopilar entradas
    for (const [key, entry] of this.memoryCache) {
      totalSize += entry.size;
      memoryEntries.push({
        key,
        timestamp: entry.timestamp,
        ttl: entry.ttl,
        size: entry.size
      });
    }

    // Eliminar entradas expiradas
    for (const entry of memoryEntries) {
      if (now - entry.timestamp > entry.ttl) {
        this.memoryCache.delete(entry.key);
        totalSize -= entry.size;
      }
    }

    // Si aún hay demasiados datos en memoria, eliminar los más antiguos
    if (totalSize > this.memoryCacheSize) {
      memoryEntries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = Math.floor(memoryEntries.length * 0.4); // Eliminar 40% más antiguo
      
      for (let i = 0; i < toRemove; i++) {
        this.memoryCache.delete(memoryEntries[i].key);
      }
      
          }
  }

  // Obtener tamaño actual del cache
  getCacheSize() {
    let total = 0;
    for (let key in localStorage) {
      if (key.startsWith(this.prefix)) {
        total += localStorage.getItem(key).length;
      }
    }
    return total;
  }

  // Limpiar cache expirado y por tamaño
  cleanup() {
    const now = Date.now();
    const cacheEntries = [];

    // Recopilar entradas del cache
    for (let key in localStorage) {
      if (key.startsWith(this.prefix)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          const isExpired = now - cached.timestamp > cached.ttl;
          
          if (isExpired) {
            localStorage.removeItem(key);
          } else {
            cacheEntries.push({
              key,
              timestamp: cached.timestamp,
              size: cached.size
            });
          }
        } catch (error) {
          localStorage.removeItem(key);
        }
      }
    }

    // Si aún hay demasiados datos, eliminar los más antiguos
    if (this.getCacheSize() > this.maxCacheSize) {
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = Math.floor(cacheEntries.length * 0.3); // Eliminar 30% más antiguo
      
      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(cacheEntries[i].key);
      }
    }
  }

  // Limpiar todo el cache
  clear() {
    for (let key in localStorage) {
      if (key.startsWith(this.prefix)) {
        localStorage.removeItem(key);
      }
    }
      }

  // Eliminar una entrada específica del cache
  delete(key) {
    // Eliminar de memoria
    this.memoryCache.delete(key);
    
    // Eliminar de localStorage
    try {
      localStorage.removeItem(key);
          } catch (error) {
          }
  }

  // Función para limpiar cache manualmente desde consola
  forceCleanup() {
        this.clear();
    this.cleanupCorruptedCache();
      }

  // Función de emergencia para limpiar todo el cache
  emergencyCleanup() {
        this.clear();
      }

  // Obtener estadísticas del cache
  getStats() {
    const stats = {
      totalEntries: 0,
      totalSize: 0,
      byType: {},
      expired: 0,
      memoryEntries: this.memoryCache.size,
      memorySize: 0
    };

    const now = Date.now();
    
    // Estadísticas del localStorage
    for (let key in localStorage) {
      if (key.startsWith(this.prefix)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          const isExpired = now - cached.timestamp > cached.ttl;
          
          if (isExpired) {
            stats.expired++;
          } else {
            stats.totalEntries++;
            stats.totalSize += cached.size;
            
            const type = key.split('_')[1];
            stats.byType[type] = (stats.byType[type] || 0) + 1;
          }
        } catch (error) {
          // Ignorar entradas corruptas
        }
      }
    }

    // Estadísticas del cache en memoria
    for (const [key, entry] of this.memoryCache) {
      stats.memorySize += entry.size;
    }

    return stats;
  }
}

// Cache específico para diferentes tipos de datos
class DataCache {
  constructor() {
    this.geoCache = new GeoCache();
  }

  // Cache para polígonos demográficos
  async getZipcodes(bounds, zoom, filters) {
    const key = this.geoCache.generateKey('zipcodes', bounds, zoom, filters);
    return this.geoCache.get(key);
  }

  async setZipcodes(bounds, zoom, filters, data) {
    const key = this.geoCache.generateKey('zipcodes', bounds, zoom, filters);
    return this.geoCache.set(key, data);
  }

  // Cache para sucursales
  async getCertainteedPoints(filters = {}) {
    const key = this.geoCache.generateKey('certainteed', null, 0, filters);
    return this.geoCache.get(key);
  }

  async setCertainteedPoints(filters, data) {
    const key = this.geoCache.generateKey('certainteed', null, 0, filters);
    return this.geoCache.set(key, data, 12 * 60 * 60 * 1000); // 12 horas para sucursales
  }

  // Cache para puntos de Apollo
  async getApolloPoints(filters = {}) {
    const key = this.geoCache.generateKey('apollo', null, 0, filters);
    return this.geoCache.get(key);
  }

  async setApolloPoints(data, filters = {}) {
    const key = this.geoCache.generateKey('apollo', null, 0, filters);
    return this.geoCache.set(key, data, 12 * 60 * 60 * 1000); // 12 horas para Apollo
  }

  // Cache para listas de filtros (estados, DMAs, etc.)
  async getFilterList(type) {
    const key = `${this.geoCache.prefix}filterList_${type}`;
    return await this.geoCache.get(key);
  }

  async setFilterList(type, data) {
    const key = `${this.geoCache.prefix}filterList_${type}`;
    return await this.geoCache.set(key, data, 7 * 24 * 60 * 60 * 1000); // 7 días para filtros
  }

  // Cache para estadísticas
  async getStatistics(filters) {
    const key = this.generateStatisticsKey(filters);
    return this.geoCache.get(key);
  }

  async setStatistics(filters, data) {
    const key = this.generateStatisticsKey(filters);
    return this.geoCache.set(key, data);
  }

  generateStatisticsKey(filters) {
    return `statistics_${JSON.stringify(filters)}`;
  }

  async clearStatistics(filters) {
    const key = this.generateStatisticsKey(filters);
    return this.geoCache.delete(key);
  }

  async getAdvancedStatistics() {
    const key = 'advanced_statistics';
    return this.geoCache.get(key);
  }

  async setAdvancedStatistics(data) {
    const key = 'advanced_statistics';
    return this.geoCache.set(key, data);
  }

  async clearAdvancedStatistics() {
    const key = 'advanced_statistics';
    return this.geoCache.delete(key);
  }

  // Métodos de utilidad
  getStats() {
    return this.geoCache.getStats();
  }

  cleanup() {
    this.geoCache.cleanup();
  }

  clear() {
    this.geoCache.clear();
  }

  // Función para limpiar cache manualmente desde consola
  forceCleanup() {
        this.geoCache.forceCleanup();
      }

  // Función de emergencia para limpiar todo el cache
  emergencyCleanup() {
        this.geoCache.emergencyCleanup();
      }
}

// Exportar instancia singleton
export const dataCache = new DataCache();

// Función global para limpiar cache desde consola
window.clearAppCache = () => {
    dataCache.forceCleanup();
  };

// Función específica para limpiar cache de Apollo
window.clearApolloCache = () => {
    const apolloKey = dataCache.geoCache.generateKey('apollo', null, 0, {});
  dataCache.geoCache.memoryCache.delete(apolloKey);
  localStorage.removeItem(apolloKey);
  };

// Función para limpiar todo el cache y forzar recarga
window.clearAllCache = () => {
    dataCache.geoCache.memoryCache.clear();
  localStorage.clear();
    window.location.reload(true);
};

// Función global para limpieza de emergencia
window.emergencyClearCache = () => {
    dataCache.emergencyCleanup();
  };

// Función global para ver estadísticas del cache
window.getCacheStats = () => {
  const stats = dataCache.getStats();
    return stats;
};

export default dataCache; 